import { Injectable, OnDestroy, EventEmitter } from '@angular/core';
import { Observable, Subject, Subscription } from 'rxjs/Rx';
import { UsersWebService } from './users.web.service';
import {
	UserDto, ActiveDirectorySettingsDto, GoogleWorkspaceSettingsDto,
	GroupDto, GroupTreeDto, GetAuthSettingsResultDto, AccountType,
	GetDirectoryUsersResultDto,
	GetDirectoryGroupsResultDto,
	GetUsersInGroupResultDto,
	GetUsersInDirectoryGroupResultDto,
	GetDirectoryUsersInDirectoryGroupResultDto,
	GetGroupsContainingUserResultDto,
	GetGroupsContainingDirectoryUserResultDto, SamlSettingsDto, DirectoryGroupDto,
} from 'UvExplorerServer.Dtos.TS/Auth';
import { HashSet, List } from 'DiscoveryEngine.TS/Utils';
import { ObjSerUtils } from 'DiscoveryEngine.TS/Serialization';
import { IdentityService } from '..';


export enum UsersServiceEventType {
	UserLoad,
	UserCreate,
	UserUpdate,
	UserDelete,
	GroupLoad,
	GroupCreate,
	GroupUpdate,
	GroupDelete,
}


export class UsersServiceEvent {
	public type: UsersServiceEventType;
	public users: UserDto[];
	public userGuids: string[];
	public groups: GroupDto[];
	public groupGuids: string[];

	constructor(type: UsersServiceEventType) {
		this.type = type;
	}
}


@Injectable()
export class UsersService implements OnDestroy {

	public adSettings: ActiveDirectorySettingsDto;
	public gwSettings: GoogleWorkspaceSettingsDto;
	public samlSettings: SamlSettingsDto;

	public users: List<UserDto>;
	public groupTree: GroupTreeDto;

	public onActiveDirectorySettingsLoaded: EventEmitter<ActiveDirectorySettingsDto>;
	public onGoogleWorkspaceSettingsLoaded: EventEmitter<GoogleWorkspaceSettingsDto>;
	public onSamlSettingsLoaded: EventEmitter<SamlSettingsDto>;
	public onUserChange: EventEmitter<UsersServiceEvent>;
	public onGroupChange: EventEmitter<UsersServiceEvent>;

	private error: boolean;

	private loginSub: Subscription;
	private logoutSub: Subscription;

	constructor(private usersWebService: UsersWebService,
		private identityService: IdentityService) {

		this.adSettings = null;
		this.gwSettings = null;
		this.samlSettings = null;

		this.users = new List<UserDto>();
		this.groupTree = new GroupTreeDto();

		this.onActiveDirectorySettingsLoaded = new EventEmitter<ActiveDirectorySettingsDto>();
		this.onGoogleWorkspaceSettingsLoaded = new EventEmitter<GoogleWorkspaceSettingsDto>();
		this.onSamlSettingsLoaded = new EventEmitter<SamlSettingsDto>();
		this.onUserChange = new EventEmitter<UsersServiceEvent>();
		this.onGroupChange = new EventEmitter<UsersServiceEvent>();

		this.error = false;

		this.loginSub = identityService.onLogin.subscribe((user: UserDto) => {
			this.getAllUsers();
			this.getAllGroups();
		});

		this.logoutSub = identityService.onLogout.subscribe(() => {
			this.users.Clear();
			this.groupTree.Clear();
		});

		this.loadAuthSettings();
		this.getAllUsers();
		this.getAllGroups();
	}

	public get hasActiveDirectorySettings(): boolean {
		return !!this.adSettings;
	}

	public get hasGoogleWorkspaceSettings(): boolean {
		return !!this.gwSettings;
	}

	public get hasSamlSettings(): boolean {
		return !!this.samlSettings;
	}

	public get hasUsers(): boolean {
		return (!!this.users && this.users.Count > 0);
	}

	public get hasGroups(): boolean {
		return (!!this.groupTree && this.groupTree.HasGroups);
	}

	private loadAuthSettings(): Observable<boolean> {

		let subject = new Subject<boolean>();

		this.getAuthSettings().subscribe(
			(result: GetAuthSettingsResultDto) => {
				this.adSettings = result.ActiveDirectorySettings;
				this.gwSettings = result.GoogleWorkspaceSettings;
				this.samlSettings = result.SamlSettings;
				this.error = false;
				subject.next(true);
				this.onActiveDirectorySettingsLoaded.emit(this.adSettings);
				this.onGoogleWorkspaceSettingsLoaded.emit(this.gwSettings);
				this.onSamlSettingsLoaded.emit(this.samlSettings);
			},
			(error) => {
				// Retry
				console.error("Retrying retrieve authentication settings");
				console.error(error);

				const RetryDelay = 2000;	// ms
				setTimeout(() => {
					this.getAuthSettings().subscribe(
						(result: GetAuthSettingsResultDto) => {
							this.adSettings = result.ActiveDirectorySettings;
							this.gwSettings = result.GoogleWorkspaceSettings;
							this.samlSettings = result.SamlSettings;
							this.error = false;
							subject.next(true);
							this.onActiveDirectorySettingsLoaded.emit(this.adSettings);
							this.onGoogleWorkspaceSettingsLoaded.emit(this.gwSettings);
							this.onSamlSettingsLoaded.emit(this.samlSettings);
						},
						(error) => {
							console.error("Could not retrieve authentication settings");
							console.error(error);

							this.adSettings = null;
							this.gwSettings = null;
							this.samlSettings = null;
							this.error = true;
							subject.error(error);
							this.onActiveDirectorySettingsLoaded.emit(this.adSettings);
							this.onGoogleWorkspaceSettingsLoaded.emit(this.gwSettings);
							this.onSamlSettingsLoaded.emit(this.samlSettings);
						});
				}, RetryDelay);
			});

		return subject.asObservable();
	}

	public ngOnDestroy(): void {
		if (this.loginSub) {
			this.loginSub.unsubscribe();
			this.loginSub = undefined;
		}
		if (this.logoutSub) {
			this.logoutSub.unsubscribe();
			this.logoutSub = undefined;
		}
	}

	public getAllUsers(): Observable<List<UserDto>> {
		let subject = new Subject<List<UserDto>>();

		this.usersWebService.getAllUsers().subscribe(
			(result: List<UserDto>) => {
				this.users = result;
				subject.next(result);
				this.onUserChange.emit(
					new UsersServiceEvent(UsersServiceEventType.UserLoad));
			},
			(error) => {
				subject.error(error);
			});

		return subject.asObservable();
	}

	public createUser(user: UserDto, addedGroups: HashSet<string>, userPassword: string): Observable<UserDto> {

		let subject = new Subject<UserDto>();

		this.usersWebService.createUser(user, addedGroups, userPassword).subscribe(
			(result: UserDto) => {
				// Add user to local users
				this.users.Add(result);

				// Add user to local groups
				addedGroups.ForEach((groupGuid: string) => {
					let group = this.groupTree.FindGroupByGuid(groupGuid);
					if (group) {
						group.MemberUserGuids.Add(user.Guid);
					}
					else {
						let userName = user.FirstName + " " + user.LastName;
						console.error(`Could not add user to local group (group not found): ${userName}, ${groupGuid}`);
					}
				});

				subject.next(result);

				let event = new UsersServiceEvent(UsersServiceEventType.UserCreate);
				event.users = [user];
				this.onUserChange.emit(event);
			},
			(error) => {
				subject.error(error);
			});

		return subject.asObservable();
	}

	public updateUser(user: UserDto, addedGroups: HashSet<string>, removedGroups: HashSet<string>,
		userPassword: string): Observable<UserDto> {

		let subject = new Subject<UserDto>();

		this.usersWebService.updateUser(user, addedGroups, removedGroups, userPassword).subscribe(
			(result: UserDto) => {
				// Local user is already updated

				// Add user to local groups
				addedGroups.ForEach((groupGuid: string) => {
					let group = this.groupTree.FindGroupByGuid(groupGuid);
					if (group) {
						group.MemberUserGuids.Add(user.Guid);
					}
					else {
						let userName = user.FirstName + " " + user.LastName;
						console.error(`Could not add user to local group (group not found): ${userName}, ${groupGuid}`);
					}
				});

				// Remove user from local groups
				removedGroups.ForEach((groupGuid: string) => {
					let group = this.groupTree.FindGroupByGuid(groupGuid);
					if (group) {
						group.MemberUserGuids.Remove(user.Guid);
					}
					else {
						let userName = user.FirstName + " " + user.LastName;
						console.error(`Could not remove user from local group (group not found): ${userName}, ${groupGuid}`);
					}
				});

				subject.next(result);

				let event = new UsersServiceEvent(UsersServiceEventType.UserUpdate);
				event.users = [user];
				this.onUserChange.emit(event);
			},
			(error) => {
				subject.error(error);
			});

		return subject.asObservable();
	}

	public updateUserSettings(user: UserDto): Observable<boolean> {	
		return this.usersWebService.updateUserSettings(user);
	}


	public deleteUser(userGuid: string): Observable<boolean> {
		let subject = new Subject<boolean>();

		this.usersWebService.deleteUser(userGuid).subscribe(
			(result: boolean) => {
				// Delete user from local users
				let userIndex = this.users.IndexOfPredicate((value: UserDto) => value.Guid === userGuid);
				if (userIndex >= 0) {
					this.users.RemoveAt(userIndex);
				}
				else {
					console.error(`Could not delete local user (not found): ${userGuid}`);
				}

				// Remove user from all local groups
				this.groupTree.Groups.ForEach((group: GroupDto) => {
					group.MemberUserGuids.Remove(userGuid);
				});

				subject.next(result);

				let event = new UsersServiceEvent(UsersServiceEventType.UserDelete);
				event.userGuids = [userGuid];
				this.onUserChange.emit(event);
			},
			(error) => {
				subject.error(error);
			});

		return subject.asObservable();
	}

	public changePassword(oldUserPassword: string, newUserPassword: string): Observable<boolean> {
		return this.usersWebService.changePassword(oldUserPassword, newUserPassword);
	}

	public getAllGroups(): Observable<GroupTreeDto> {
		let subject = new Subject<GroupTreeDto>();

		this.usersWebService.getAllGroups().subscribe(
			(result: GroupTreeDto) => {
				this.groupTree = result;
				subject.next(result);
				this.onGroupChange.emit(
					new UsersServiceEvent(UsersServiceEventType.GroupLoad));
			},
			(error) => {
				subject.error(error);
			});

		return subject.asObservable();
	}

	public createGroup(group: GroupDto, parentGuid: string, position: number): Observable<GroupDto> {
		let subject = new Subject<GroupDto>();

		this.usersWebService.createGroup(group, parentGuid, position).subscribe(
			(result: GroupDto) => {
				this.addGroupToTree(group, parentGuid);

				subject.next(result);

				let event = new UsersServiceEvent(UsersServiceEventType.GroupCreate);
				event.groups = [group];
				this.onGroupChange.emit(event);
			},
			(error) => {
				subject.error(error);
			});

		return subject.asObservable();
	}

	public updateGroup(group: GroupDto): Observable<GroupDto> {
		let subject = new Subject<GroupDto>();

		this.usersWebService.updateGroup(group).subscribe(
			(result: GroupDto) => {
				// Local group is already updated

				subject.next(result);

				let event = new UsersServiceEvent(UsersServiceEventType.GroupUpdate);
				event.groups = [group];
				this.onGroupChange.emit(event);
			},
			(error) => {
				subject.error(error);
			});

		return subject.asObservable();
	}

	public deleteGroup(groupGuid: string, parentGuid: string): Observable<boolean> {
		let subject = new Subject<boolean>();

		this.usersWebService.deleteGroup(groupGuid, parentGuid).subscribe(
			(result: boolean) => {
				this.removeGroupFromTree(groupGuid);

				subject.next(result);

				let event = new UsersServiceEvent(UsersServiceEventType.GroupDelete);
				event.groupGuids = [groupGuid];
				this.onGroupChange.emit(event);
			},
			(error) => {
				subject.error(error);
			});

		return subject.asObservable();
	}

	private addGroupToTree(group: GroupDto, parentGuid: string): void {
		let parentGroup = this.groupTree.FindGroupByGuid(parentGuid);
		if (parentGroup) {
			parentGroup.MemberGroupGuids.Add(group.Guid);
			this.groupTree.AddGroup(group);
		}
		else {
			console.error(`Could not add local group to tree (parent not found): ${group.Name}, ${parentGuid}`);
		}
	}

	private removeGroupFromTree(groupGuid: string): void {
		let group = this.groupTree.FindGroupByGuid(groupGuid);
		if (group) {
			let parentGroup = this.groupTree.FindGroupParent(groupGuid);
			if (parentGroup) {
				parentGroup.MemberGroupGuids.Remove(groupGuid);
			}
			else {
				console.error(`Could not remove local group from tree (parent not found): ${group.Name}`);
			}

			this.groupTree.RemoveGroup(groupGuid);
		}
		else {
			console.error(`Could not delete local group (not found): ${groupGuid}`);
		}
	}

	public getGroupsContainingUser(userGuid: string): Observable<GetGroupsContainingUserResultDto> {
		return this.usersWebService.getGroupsContainingUser(userGuid);
	}

	public getGroupsContainingDirectoryUser(accountType: AccountType, userDirectoryId: string,
		userDirectoryId_2: string): Observable<GetGroupsContainingDirectoryUserResultDto> {
		return this.usersWebService.getGroupsContainingDirectoryUser(accountType, userDirectoryId, userDirectoryId_2);
	}

	public addDirectoryGroupToCache(accountType: AccountType, domainName: string, groupDirectoryId: string,
		groupDirectoryId_2: string): Observable<boolean> {
		return this.usersWebService.addDirectoryGroupToCache(accountType, domainName, groupDirectoryId,
			groupDirectoryId_2);
	}

	public getUsersInGroup(groupGuid: string): Observable<GetUsersInGroupResultDto> {
		return this.usersWebService.getUsersInGroup(groupGuid);
	}

	public getUsersInDirectoryGroup(accountType: AccountType, domainName: string, groupDirectoryId: string,
		groupDirectoryId_2: string): Observable<GetUsersInDirectoryGroupResultDto> {
		return this.usersWebService.getUsersInDirectoryGroup(accountType, domainName, groupDirectoryId,
			groupDirectoryId_2);
	}

	public getDirectoryUsersInDirectoryGroup(accountType: AccountType, domainName: string,
		groupDirectoryId: string, groupDirectoryId_2: string): Observable<GetDirectoryUsersInDirectoryGroupResultDto> {
		return this.usersWebService.getDirectoryUsersInDirectoryGroup(accountType, domainName, groupDirectoryId,
			groupDirectoryId_2);
	}

	private getAuthSettings(): Observable<GetAuthSettingsResultDto> {
		return this.usersWebService.getAuthSettings();
	}

	public setAuthSettings(adSettings: ActiveDirectorySettingsDto,
		gwSettings: GoogleWorkspaceSettingsDto, samlSettings: SamlSettingsDto): Observable<boolean> {

		let subject = new Subject<boolean>();

		this.usersWebService.setAuthSettings(adSettings, gwSettings, samlSettings).subscribe(
			(result: boolean) => {
				if (adSettings) {
					this.adSettings = new ActiveDirectorySettingsDto();
					ObjSerUtils.Copy(adSettings, this.adSettings);
					this.onActiveDirectorySettingsLoaded.emit(this.adSettings);
				}

				if (gwSettings) {
					this.gwSettings = new GoogleWorkspaceSettingsDto();
					ObjSerUtils.Copy(gwSettings, this.gwSettings);
					this.onGoogleWorkspaceSettingsLoaded.emit(this.gwSettings);
				}

				if (samlSettings) {
					this.samlSettings = new SamlSettingsDto();
					ObjSerUtils.Copy(samlSettings, this.samlSettings);
					this.onSamlSettingsLoaded.emit(this.samlSettings);
				}

				subject.next(true);
			},
			(error) => {
				console.error(error);
				subject.error(error);
			});

		return subject.asObservable();
	}

	public getDirectoryUsers(accountType: AccountType, domainName: string, nameFilter: string,
		permanentIdFilter: string, ephemeralIdFilter: string): Observable<GetDirectoryUsersResultDto> {

		return this.usersWebService.getDirectoryUsers(accountType, domainName, nameFilter,
			permanentIdFilter, ephemeralIdFilter);
	}

	public getDirectoryGroups(accountType: AccountType, domainName: string, nameFilter: string,
		permanentIdFilter: string, ephemeralIdFilter: string): Observable<GetDirectoryGroupsResultDto> {

		return this.usersWebService.getDirectoryGroups(accountType, domainName,
			nameFilter, permanentIdFilter, ephemeralIdFilter);
	}

	public syncDirectoryGroups(accountType: AccountType): Observable<any> {

		return this.usersWebService.syncDirectoryGroups(accountType);
	}

	public syncDirectoryGroup(group: GroupDto): Observable<DirectoryGroupDto> {

		let subject = new Subject<DirectoryGroupDto>();

		this.usersWebService.syncDirectoryGroup(group.Guid).subscribe(
			() => {
				this.getDirectoryGroups(group.AccountType, group.DirectoryDomainName,
					"", group.DirectoryId, group.DirectoryId_2).subscribe(
						(result: GetDirectoryGroupsResultDto) => {
							if (result.HasGroups && result.Groups.Groups.Count == 1) {
								let dirGroup: DirectoryGroupDto = result.Groups.Groups.First();
								subject.next(dirGroup);
							}
							else {
								let error = "Could not get directory group: multiple results";
								console.error(error);
								subject.error(error);
							}
						},
						(error) => {
							console.error("Could not get directory group" + error);
							subject.error(error);
						});
			},
			(error) => {
				console.error("Could not sync directory group" + error);
				subject.error(error);
            }
		);

		return subject.asObservable();
	}
}
